import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface CreditReason {
  id: string;
  reason_name: string;
  description: string | null;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export const useCreditReasons = () => {
  return useQuery({
    queryKey: ['credit-reasons'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('credit_reasons')
        .select('*')
        .eq('is_active', true)
        .order('display_order');
      
      if (error) throw error;
      return data as CreditReason[];
    },
  });
};

export const useCreateCreditReason = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (reason: Omit<CreditReason, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('credit_reasons')
        .insert([reason])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-reasons'] });
      toast({
        title: "Success",
        description: "Credit reason created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create credit reason",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateCreditReason = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<CreditReason> & { id: string }) => {
      const { data, error } = await supabase
        .from('credit_reasons')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-reasons'] });
      toast({
        title: "Success",
        description: "Credit reason updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update credit reason",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteCreditReason = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('credit_reasons')
        .update({ is_active: false })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-reasons'] });
      toast({
        title: "Success",
        description: "Credit reason deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete credit reason",
        variant: "destructive",
      });
    },
  });
};